import React, { useState } from "react";
import { isFunction } from "lodash-es";
import { Progress } from "reactstrap";
import { extractErrorMessage } from "../util/api";
import { useJobStatus } from "../util/useJobStatus";

// A zero-percent job is treated as indeterminate until it reports progress.
export const JobProgress = ({ pctComplete = 0, message, label }) => {
  const indefinite = pctComplete === 0;
  const caption = [message || label, indefinite ? null : `${pctComplete}%`].filter(Boolean).join(" ");

  return (
    <div className="d-flex align-items-center gap-2" style={{ minWidth: "12rem" }}>
      <Progress className="flex-grow-1" animated striped={indefinite} value={indefinite ? 100 : pctComplete} />
      {caption && <small className="text-muted text-nowrap">{caption}</small>}
    </div>
  );
};

/**
 * Starts and tracks a StatusTracked job, restoring children after it becomes terminal.
 * Children may be a render prop:
 *   {(start, { starting }) => <Button onClick={start} disabled={starting}>Export</Button>}
 * renderRunning overrides the default progress UI; outcome UI belongs to the parent.
 */
export const JobStatusTracker = ({ start, onFinished, onFailed, label, renderRunning, intervalMs, children }) => {
  const [jobId, setJobId] = useState(null);
  const [starting, setStarting] = useState(false);

  const jobStatus = useJobStatus(jobId, {
    intervalMs,
    onFinished: (finalStatus) => {
      setJobId(null);
      onFinished?.(finalStatus);
    },
    onFailed: (finalStatus) => {
      setJobId(null);
      onFailed?.(finalStatus);
    },
  });

  const handleStart = async () => {
    if (starting || jobId) return;

    setStarting(true);
    try {
      const newJobId = await start();
      if (!newJobId) {
        onFailed?.({ status: "failed", error: "The job could not be started: no job id was returned." });
        return;
      }
      setJobId(newJobId);
    } catch (err) {
      onFailed?.({ status: "failed", error: extractErrorMessage(err) });
    } finally {
      setStarting(false);
    }
  };

  if (jobId) {
    if (renderRunning) return renderRunning(jobStatus);

    return <JobProgress pctComplete={jobStatus.pctComplete} message={jobStatus.message} label={label} />;
  }

  return isFunction(children) ? children(handleStart, { starting }) : (children ?? null);
};
